import { Box, Flex, Image, Link, Text, VStack } from '@chakra-ui/react';
import type { ReactNode } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import logoWhiteImage from '@/travo/assets/images/shared/logo-white-with-text.svg';
import { Container, Eyebrow } from '@/travo/components/ui/postmark';
import { localizePath, useCopy } from '@/travo/i18n';
import { ROUTES } from '@/travo/routes';
import { COMMUNITY_LINKS } from '@/travo/config';
import { useNavItems } from '../Header/useNavItems';

const linkStyles = { color: 'sand', py: '6px', textDecoration: 'none', _hover: { color: 'white' } } as const;

interface ColumnProps {
  title: string;
  children: ReactNode;
}

const Column = ({ title, children }: ColumnProps) => (
  <VStack align="flex-start" gap="6px">
    <Eyebrow color="sun" fontSize="12px">
      {title}
    </Eyebrow>
    {children}
  </VStack>
);

interface ExternalLinkProps {
  href: string;
  children: ReactNode;
}

const ExternalLink = ({ href, children }: ExternalLinkProps) => (
  <Link href={href} target="_blank" rel="noopener noreferrer" {...linkStyles}>
    {children}
  </Link>
);

const Footer = () => {
  const copy = useCopy();
  const { links, locale } = useNavItems();

  return (
    <Box as="footer" bg="ink" color="sand">
      <Container pt="64px" pb="40px" display="flex" flexDirection="column" gap="48px">
        <Flex wrap="wrap" justify="space-between" gap="40px">
          <VStack align="flex-start" gap="14px" maxW="320px">
            <Image src={logoWhiteImage} h="44px" alt="Travo" loading="lazy" />
            <Text fontSize="16px">{copy.footer.tagline}</Text>
          </VStack>

          <Flex wrap="wrap" gap={{ base: '32px', md: '72px' }} fontSize="15px">
            <Column title={copy.footer.product}>
              {links.map((link) => (
                <Link key={link.to} asChild {...linkStyles}>
                  <RouterLink to={link.to}>{link.label}</RouterLink>
                </Link>
              ))}
            </Column>
            <Column title={copy.footer.community}>
              <ExternalLink href={COMMUNITY_LINKS.telegram}>Telegram</ExternalLink>
            </Column>
            <Column title={copy.footer.legal}>
              <Link asChild {...linkStyles}>
                <RouterLink to={localizePath(locale, ROUTES.privacy)}>{copy.footer.privacy}</RouterLink>
              </Link>
              <Link asChild {...linkStyles}>
                <RouterLink to={localizePath(locale, ROUTES.terms)}>{copy.footer.terms}</RouterLink>
              </Link>
            </Column>
          </Flex>
        </Flex>

        <Flex
          wrap="wrap"
          justify="space-between"
          align="center"
          gap="12px"
          borderTop="1px solid"
          borderColor="sand/25"
          pt="24px"
          fontFamily="mono"
          fontSize="13px"
          letterSpacing="0.06em"
        >
          <Link asChild color="sand" textDecoration="none" _hover={{ color: 'white' }}>
            {/* Travo is a Travo Labs app: the copyright line leads to the studio's page */}
            <RouterLink to="/">
              © {__BUILD_YEAR__} {copy.footer.rights}
            </RouterLink>
          </Link>
          <Text fontFamily="heading" fontSize="22px" color="sky" textShadow="1px 1px 0 {colors.sun}">
            Travo
          </Text>
        </Flex>
      </Container>
    </Box>
  );
};

export default Footer;
